import { useEffect, useMemo, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';
import type {
  AttachmentDto,
  AvailableLawyerDto,
  CaseDetailDto,
  ConversationAudience,
  ConversationDto,
  ConversationTopic,
  LawyerChangeReason,
  LawyerChangeRequestDto,
  LawyerSummaryDto,
  MessageDto,
} from '@lex/contracts';
import { useQuery } from '@tanstack/react-query';
import { Button, Spinner } from '@lex/ui';
import {
  AttachIcon,
  CheckCircleIcon,
  CheckIcon,
  LockIcon,
  PersonIcon,
  PlusIcon,
  SendIcon,
} from '../components/icons';
import { Orb } from '../components/home/primitives';
import { apiClient } from '../lib/api';
import { connectSocket } from '../lib/socket';

const TOPICS: Array<{ value: ConversationTopic; label: string }> = [
  { value: 'prizyv', label: 'Призыв' },
  { value: 'mobilizaciya', label: 'Мобилизация' },
  { value: 'vyplaty', label: 'Выплаты' },
  { value: 'medicina', label: 'Медицинские вопросы' },
  { value: 'otkaz', label: 'Обжалование' },
  { value: 'other', label: 'Другое' },
];

const CHANGE_REASONS: Array<{ value: LawyerChangeReason; title: string; sub: string }> = [
  { value: 'no_contact', title: 'Не нашли общий язык', sub: 'Подберём юриста с другим подходом' },
  { value: 'slow_response', title: 'Долго отвечает', sub: 'Передадим обращение свободному юристу' },
  { value: 'another_lawyer', title: 'Хочу другого юриста', sub: 'Можно выбрать специалиста из списка' },
  { value: 'style_mismatch', title: 'Не подходит стиль общения', sub: 'Учтите это при выборе нового юриста' },
  { value: 'other', title: 'Другая причина', sub: 'Опишите ситуацию в комментарии' },
];

const CLOSED: ConversationDto['status'][] = [
  'closed_by_client',
  'auto_closed',
  'closed_not_relevant',
  'closed_blocked',
];

const AUDIENCES: Array<{ value: ConversationAudience; title: string; sub: string }> = [
  { value: 'prizyvnik', title: 'Призывнику', sub: 'Вопрос про призыв и категорию годности' },
  { value: 'voennosluzhashchiy', title: 'Военнослужащему', sub: 'Выплаты, отпуск, увольнение' },
  { value: 'relative', title: 'Для близкого человека', sub: 'Помогаю родственнику с вопросом' },
];

const FEEDBACK_REASONS = [
  {
    value: 'not_clear',
    title: 'Не понял ответ',
    sub: 'Юрист объяснит проще, это бесплатно в рамках обращения',
  },
  {
    value: 'not_enough_info',
    title: 'Не хватает информации',
    sub: 'Дополните вопрос или документы — юрист вернётся к ответу',
  },
  {
    value: 'wrong_situation',
    title: 'Ответ не по моей ситуации',
    sub: 'Юрист перечитает вопрос и пересмотрит ответ',
  },
  {
    value: 'refund',
    title: 'Хочу вернуть деньги',
    sub: 'Опишите причину, мы рассмотрим обращение',
  },
];

function statusTone(status: ConversationDto['status']): string {
  if (status === 'answered') return 'bg-success-soft text-success';
  if (status === 'docs_requested') return 'bg-[#FBF3DE] text-[#7A5C14]';
  if (status === 'closed_by_client' || status === 'auto_closed' || status === 'closed_not_relevant' || status === 'closed_blocked') {
    return 'border border-line bg-soft text-muted';
  }
  if (status === 'reopened' || status === 'client_clarification') return 'bg-accent-soft text-accent';
  return 'bg-accent-soft text-accent';
}

function statusLabel(status: ConversationDto['status']): string {
  const labels: Record<ConversationDto['status'], string> = {
    new: 'Принято',
    in_progress: 'Юрист изучает',
    waiting_lawyer: 'Ждёт ответа юриста',
    docs_requested: 'Юрист просит документы',
    answered: 'Ответ готов',
    client_clarification: 'Уточнение клиента',
    reopened: 'Возвращено в работу',
    closed_by_client: 'Обращение закрыто',
    auto_closed: 'Обращение закрыто',
    closed_not_relevant: 'Вопрос не по профилю',
    closed_blocked: 'Доступ закрыт',
  };
  return labels[status];
}

function StageBar({ status }: { status: ConversationDto['status'] }) {
  const steps = ['Заявка принята', 'Юрист изучает', 'Ответ готов'];
  const current =
    status === 'answered' || CLOSED.includes(status)
      ? 3
      : status === 'new'
        ? 1
        : 2;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {steps.map((label, index) => {
        const done = index + 1 < current;
        const active = index + 1 === current;
        return (
          <div key={label} className="flex items-center gap-2">
            <span
              className={`flex h-[22px] w-[22px] items-center justify-center rounded-full ${
                done || active ? 'bg-accent text-white' : 'border-2 border-line text-muted'
              }`}
            >
              {done ? (
                <CheckIcon className="h-3 w-3" />
              ) : active && status === 'new' && index === 0 ? (
                <Orb className="h-[22px] w-[22px]" />
              ) : (
                <span className="text-[11px] font-semibold">{index + 1}</span>
              )}
            </span>
            <span
              className={`text-[14px] leading-[18px] ${
                active || done ? 'font-medium text-ink' : 'text-muted'
              }`}
            >
              {label}
            </span>
            {index < steps.length - 1 ? (
              <span className={`h-0.5 w-[30px] rounded ${done ? 'bg-accent' : 'bg-line'}`} />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function ChatPage() {
  const [conversations, setConversations] = useState<ConversationDto[]>([]);
  const [active, setActive] = useState<ConversationDto | null>(null);
  const [messages, setMessages] = useState<MessageDto[]>([]);
  const [draft, setDraft] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [topic, setTopic] = useState<ConversationTopic>('prizyv');
  const [responseHours, setResponseHours] = useState<1 | 3 | 5>(1);
  const [audience, setAudience] = useState<ConversationAudience>('prizyvnik');
  const [relativeConsent, setRelativeConsent] = useState(false);
  const [newError, setNewError] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const createKeyRef = useRef<string | null>(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState('not_clear');
  const [feedbackComment, setFeedbackComment] = useState('');
  const [ratingBusy, setRatingBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [expandedAnswers, setExpandedAnswers] = useState<Record<string, boolean>>({});
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [typing, setTyping] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lawyer, setLawyer] = useState<LawyerSummaryDto | null>(null);
  const [lawyers, setLawyers] = useState<AvailableLawyerDto[]>([]);
  const [changeOpen, setChangeOpen] = useState(false);
  const [changeRequest, setChangeRequest] = useState<LawyerChangeRequestDto | null>(null);
  const [changeReason, setChangeReason] = useState<LawyerChangeReason>('no_contact');
  const [changeComment, setChangeComment] = useState('');
  const [preferredLawyerId, setPreferredLawyerId] = useState('');
  const [changeBusy, setChangeBusy] = useState(false);
  const [changeNotice, setChangeNotice] = useState<string | null>(null);
  const subscriptionQuery = useQuery({
    queryKey: ['chat', 'subscription'],
    queryFn: () => apiClient.chat.subscription(),
  });
  const [availableLawyers, setAvailableLawyers] = useState<number | null>(null);
  const [caseOpen, setCaseOpen] = useState(false);
  const [caseDetail, setCaseDetail] = useState<CaseDetailDto | null>(null);
  const [caseComment, setCaseComment] = useState('');
  const [caseBusy, setCaseBusy] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sendTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const activeIdRef = useRef<string | null>(null);
  const messagesRef = useRef<HTMLDivElement | null>(null);

  const clearSendTimer = (clientMessageId: string): void => {
    const timer = sendTimers.current.get(clientMessageId);
    if (timer) clearTimeout(timer);
    sendTimers.current.delete(clientMessageId);
  };

  const loadConversations = async (): Promise<ConversationDto[]> => {
    const res = await apiClient.chat.listConversations({ page: 1 });
    setConversations(res.data);
    return res.data;
  };

  const open = async (conversation: ConversationDto): Promise<void> => {
    setActive(conversation);
    activeIdRef.current = conversation.id;
    const res = await apiClient.chat.listMessages(conversation.id);
    setMessages([...res.data].reverse());
    setLawyer(conversation.lawyer ?? (await apiClient.chat.currentLawyer().catch(() => null)));
    socketRef.current?.emit('conversation:join', conversation.id);
    await apiClient.chat.markRead(conversation.id).catch(() => undefined);
  };

  useEffect(() => {
    const socket = connectSocket();
    socketRef.current = socket;

    socket.on('message:new', (payload: MessageDto) => {
      if (payload.clientMessageId) clearSendTimer(payload.clientMessageId);
      setMessages((prev) => {
        if (payload.conversationId !== activeIdRef.current) return prev;
        const index = payload.clientMessageId
          ? prev.findIndex((message) => message.clientMessageId === payload.clientMessageId)
          : -1;
        if (index >= 0) {
          const copy = [...prev];
          copy[index] = payload;
          return copy;
        }
        if (prev.some((message) => message.id === payload.id)) return prev;
        return [...prev, payload];
      });
      if (payload.conversationId === activeIdRef.current) {
        void apiClient.chat.markRead(payload.conversationId).catch(() => undefined);
      }
      void loadConversations().catch(() => undefined);
    });
    socket.on(
      'message:ack',
      (payload: { clientMessageId: string; messageId: string; createdAt: string }) => {
        clearSendTimer(payload.clientMessageId);
        setMessages((prev) =>
          prev.map((message) =>
            message.clientMessageId === payload.clientMessageId
              ? { ...message, id: payload.messageId, status: 'sent', createdAt: payload.createdAt }
              : message
          )
        );
      }
    );
    socket.on('message:failed', (payload: { clientMessageId: string; message: string }) => {
      clearSendTimer(payload.clientMessageId);
      setMessages((prev) =>
        prev.map((message) =>
          message.clientMessageId === payload.clientMessageId
            ? { ...message, status: 'failed' }
            : message
        )
      );
      setError(payload.message);
    });
    socket.on('typing', (payload: { conversationId: string; actorType: string; isTyping: boolean }) => {
      if (payload.conversationId === activeIdRef.current && payload.actorType !== 'user') {
        setTyping(payload.isTyping);
      }
    });
    socket.on('lawyer:change_request:updated', (payload: LawyerChangeRequestDto) => {
      if (payload.status === 'approved') {
        setChangeRequest(null);
        setChangeOpen(false);
        setChangeNotice(
          payload.newLawyerName
            ? `Юриста заменили: ${payload.newLawyerName}. Все активные обращения переданы.`
            : 'Юриста заменили. Все активные обращения переданы.'
        );
        if (activeIdRef.current === payload.conversationId) {
          void apiClient.chat
            .getConversation(payload.conversationId)
            .then((conversation) => open(conversation))
            .catch(() => undefined);
        }
      } else if (payload.status === 'rejected') {
        setChangeRequest(null);
        setChangeOpen(false);
        setError('Заявка на смену юриста отклонена. Юрист свяжется с вами.');
      }
    });
    socket.on('conversation:updated', (payload: { conversationId: string; status?: string }) => {
      setConversations((prev) =>
        prev.map((item) =>
          item.id === payload.conversationId
            ? { ...item, status: (payload.status as ConversationDto['status']) ?? item.status }
            : item
        )
      );
      if (payload.status) {
        setActive((prev) =>
          prev && prev.id === payload.conversationId
            ? { ...prev, status: payload.status as ConversationDto['status'] }
            : prev
        );
      }
    });

    void loadConversations()
      .then((data) => {
        if (!activeIdRef.current && data[0]) void open(data[0]);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));

    const heartbeat = setInterval(() => socket.emit('presence:ping'), 30_000);

    return () => {
      clearInterval(heartbeat);
      socket.disconnect();
      for (const timer of sendTimers.current.values()) clearTimeout(timer);
      sendTimers.current.clear();
    };
  }, []);

  useEffect(() => {
    if (active) {
      activeIdRef.current = active.id;
      setLawyer(active.lawyer);
    }
  }, [active?.id]);

  useEffect(() => {
    if (!changeNotice) return;
    const timer = setTimeout(() => setChangeNotice(null), 6000);
    return () => clearTimeout(timer);
  }, [changeNotice]);

  useEffect(() => {
    const container = messagesRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [messages.length, typing]);

  const createConversation = async (): Promise<void> => {
    if (creating) return;
    setNewError(null);
    if (audience === 'relative' && !relativeConsent) {
      setNewError('Подтвердите, что близкий человек знает и согласен передать документы.');
      return;
    }
    const clientMessageId = createKeyRef.current ?? crypto.randomUUID();
    createKeyRef.current = clientMessageId;
    setCreating(true);
    try {
      const created = await apiClient.chat.createConversation({
        topic,
        responseHours,
        audience,
        relativeConsent: audience === 'relative' ? relativeConsent : false,
        clientMessageId,
      });
      createKeyRef.current = null;
      setConversations((prev) => [created, ...prev]);
      setNewOpen(false);
      await open(created);
    } catch (err) {
      setNewError(err instanceof Error ? err.message : 'Не удалось создать обращение');
    } finally {
      setCreating(false);
    }
  };

  const applyConversation = (updated: ConversationDto): void => {
    setActive(updated);
    setConversations((prev) =>
      prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item))
    );
  };

  const rateAnswer = async (helped: boolean, reason?: string): Promise<void> => {
    if (!active) return;
    setRatingBusy(true);
    setError(null);
    try {
      const updated = await apiClient.chat.rateConversation(active.id, {
        helped,
        ...(helped ? {} : { reason: reason ?? feedbackReason }),
        ...(!helped && feedbackComment.trim() ? { comment: feedbackComment.trim() } : {}),
      });
      applyConversation(updated);
      setFeedbackOpen(false);
      setFeedbackComment('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить оценку');
    } finally {
      setRatingBusy(false);
    }
  };

  const uploadFiles = async (conversationId: string): Promise<AttachmentDto[]> => {
    const result: AttachmentDto[] = [];
    for (const file of files) result.push(await apiClient.media.uploadFile(file, conversationId));
    return result;
  };

  const send = async (text: string, resend?: MessageDto): Promise<void> => {
    if (!active) return;
    const bodyText = text.trim();
    if (!bodyText && files.length === 0) return;
    const clientMessageId = resend?.clientMessageId ?? crypto.randomUUID();
    setSending(true);
    setError(null);
    try {
      let uploaded: AttachmentDto[] = resend?.attachments ?? [];
      if (!resend && files.length > 0) uploaded = await uploadFiles(active.id);
      const optimistic: MessageDto = {
        id: resend?.id ?? clientMessageId,
        conversationId: active.id,
        clientMessageId,
        senderId: 'me',
        senderRole: 'client',
        type: uploaded.length > 0 ? 'file' : 'text',
        text: bodyText || null,
        answer: null,
        attachments: uploaded,
        readBy: [],
        status: 'pending',
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) =>
        resend
          ? prev.map((message) =>
              message.clientMessageId === clientMessageId ? optimistic : message
            )
          : [...prev, optimistic]
      );
      setDraft('');
      setFiles([]);
      clearSendTimer(clientMessageId);
      sendTimers.current.set(
        clientMessageId,
        setTimeout(() => {
          setMessages((prev) =>
            prev.map((message) =>
              message.clientMessageId === clientMessageId && message.status === 'pending'
                ? { ...message, status: 'failed' }
                : message
            )
          );
          setError('Не удалось отправить сообщение. Свяжитесь с тех. поддержкой');
        }, 20000)
      );
      await apiClient.chat.sendMessage(active.id, {
        clientMessageId,
        ...(bodyText ? { text: bodyText } : {}),
        ...(uploaded.length > 0 ? { attachmentKeys: uploaded.map((item) => item.key) } : {}),
      });
    } catch (err) {
      clearSendTimer(clientMessageId);
      setMessages((prev) =>
        prev.map((message) =>
          message.clientMessageId === clientMessageId ? { ...message, status: 'failed' } : message
        )
      );
      setError(err instanceof Error ? err.message : 'Не удалось отправить сообщение');
    } finally {
      setSending(false);
    }
  };

  const emitTyping = (value: boolean): void => {
    if (!active) return;
    socketRef.current?.emit('typing', { conversationId: active.id, isTyping: value });
    if (typingTimer.current) clearTimeout(typingTimer.current);
    if (value) typingTimer.current = setTimeout(() => emitTyping(false), 1600);
  };

  const openChange = async (): Promise<void> => {
    try {
      const [list, pending] = await Promise.all([
        apiClient.chat.availableLawyers(),
        apiClient.chat.myLawyerChangeRequest(),
      ]);
      setLawyers(list);
      setChangeRequest(pending);
      setChangeOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось открыть заявку');
    }
  };

  const submitChangeRequest = async (): Promise<void> => {
    if (!active) return;
    setChangeBusy(true);
    try {
      const request = await apiClient.chat.requestLawyerChange({
        conversationId: active.id,
        reason: changeReason,
        ...(changeComment.trim() ? { comment: changeComment.trim() } : {}),
        ...(preferredLawyerId ? { preferredLawyerId } : {}),
      });
      setChangeRequest(request);
      setChangeComment('');
      setPreferredLawyerId('');
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отправить заявку');
    } finally {
      setChangeBusy(false);
    }
  };

  const cancelChangeRequest = async (): Promise<void> => {
    if (!changeRequest) return;
    setChangeBusy(true);
    try {
      await apiClient.chat.cancelLawyerChangeRequest(changeRequest.id);
      setChangeRequest(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отменить заявку');
    } finally {
      setChangeBusy(false);
    }
  };

  const openCase = async (): Promise<void> => {
    if (!active) return;
    try {
      const detail = await apiClient.chat.myCase(active.id);
      setCaseDetail(detail);
      setCaseComment('');
      setCaseOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось открыть дело');
    }
  };

  const commentCaseDraft = async (): Promise<void> => {
    if (!active || !caseComment.trim()) return;
    setCaseBusy(true);
    try {
      setCaseDetail(await apiClient.chat.commentCaseDraft(active.id, { text: caseComment.trim() }));
      setCaseComment('');
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отправить правки');
    } finally {
      setCaseBusy(false);
    }
  };

  const closeConversation = async (): Promise<void> => {
    if (!active) return;
    try {
      await apiClient.chat.closeConversation(active.id);
      const updated = await apiClient.chat.getConversation(active.id);
      setCloseOpen(false);
      await open(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось закрыть обращение');
    }
  };

  const reopenConversation = async (): Promise<void> => {
    if (!active) return;
    try {
      await apiClient.chat.reopenConversation(active.id);
      const updated = await apiClient.chat.getConversation(active.id);
      await open(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось вернуть обращение');
    }
  };

  const downloadAnswer = (message: MessageDto): void => {
    const answer = message.answer;
    if (!answer) return;
    const lines = [
      'LEX.AI — памятка по обращению',
      '',
      answer.summary,
      '',
      'ЧТО ДЕЛАТЬ ДАЛЬШЕ',
      ...answer.steps.map((step, index) => `${index + 1}. ${step}`),
      ...(answer.deadline ? ['', `Срок: ${answer.deadline}`] : []),
      ...(answer.reminder ? ['', answer.reminder] : []),
      '',
      'Ответ проверил и подписал юрист',
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'lex-ai-pamyatka.txt';
    link.click();
    URL.revokeObjectURL(url);
  };

  const hasClientDocuments = messages.some(
    (message) => message.senderRole === 'client' && message.attachments.length > 0
  );
  const firstClientMessage = messages.find((message) => message.senderRole === 'client');

  const title = useMemo(
    () => (conversation: ConversationDto) =>
      conversation.subject ?? conversation.lastMessage?.text ?? TOPICS.find((t) => t.value === conversation.topic)?.label ?? 'Диалог',
    []
  );

  useEffect(() => {
    if (!active || active.lawyerId) {
      setAvailableLawyers(null);
      return;
    }
    void apiClient.chat
      .availableLawyers()
      .then((list) => setAvailableLawyers(list.length))
      .catch(() => setAvailableLawyers(null));
  }, [active?.id, active?.lawyerId]);

  if (loading) return <Spinner />;

  const subscription = subscriptionQuery.data;
  const subscriptionNotice = (() => {
    if (!subscription?.expiresAt) return null;
    const left = new Date(subscription.expiresAt).getTime() - Date.now();
    if (left <= 0) return null;
    if (left <= 3 * 60 * 60 * 1000) {
      return `Тариф «${subscription.planName}» закончится через ${Math.max(
        1,
        Math.round(left / (60 * 60 * 1000))
      )} ч. Продлите, чтобы юрист продолжил работу.`;
    }
    if (left <= 24 * 60 * 60 * 1000) {
      return `Тариф «${subscription.planName}» заканчивается сегодня. Продлите заранее, чтобы не потерять доступ к чату.`;
    }
    return null;
  })();

  const chatNotice = (() => {
    if (!active) return null;
    if (!active.lawyerId && active.status === 'new') {
      return availableLawyers === 0
        ? 'Сейчас нет юристов на смене. Обращение в очереди — ответим в рабочие часы 9:00–21:00.'
        : 'Подбираем юриста. Обычно это занимает несколько минут в рабочее время.';
    }
    if (active.answerDueAt && !CLOSED.includes(active.status) && active.status !== 'answered') {
      if (new Date(active.answerDueAt).getTime() < Date.now()) {
        return 'Ответ задерживается. Юрист вернётся с ответом, срок уже под контролем.';
      }
    }
    return null;
  })();

  const activeConversations = conversations.filter((item) => !CLOSED.includes(item.status));
  const historyConversations = conversations.filter((item) => CLOSED.includes(item.status));
  const quickReplies = [
    'Помогите с отсрочкой от призыва',
    'Положена ли мне выплата военнослужащему',
    'Как обжаловать решение комиссии',
  ];

  return (
    <div className="mx-auto w-full max-w-[1280px]">
      <div className="flex min-h-[760px] flex-col overflow-hidden rounded-[32px] border-[1.5px] border-white/85 bg-white/56 shadow-chat backdrop-blur-[18px]">
        <header className="flex flex-wrap items-center gap-2.5 border-b border-line bg-white/35 px-3 py-3 md:flex-nowrap md:gap-3.5 md:px-7 md:py-[18px]">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent md:h-11 md:w-11">
            <PersonIcon className="h-5 w-5 md:h-6 md:w-6" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[17px] font-semibold leading-[22px] text-ink">
              {lawyer?.name ?? 'Юрист подбирается'}
            </div>
            <div className="truncate text-[14px] leading-[18px] text-muted">
              {lawyer?.title ?? 'Военный юрист'} · {lawyer?.specializations?.slice(0, 2).join(' · ') || 'призыв, выплаты'}
            </div>
          </div>
          <span
            className={`hidden items-center gap-2 rounded-lg px-3 py-2 text-[14px] font-semibold sm:inline-flex ${
              lawyer?.online ? 'bg-success-soft text-success' : 'bg-soft text-muted'
            }`}
          >
            <span className={`h-2 w-2 rounded-full ${lawyer?.online ? 'bg-success' : 'bg-muted-400'}`} />
            {lawyer?.online ? 'на связи до 21:00' : 'ответит в рабочее время'}
          </span>
          {active && !CLOSED.includes(active.status) ? (
            <button
              type="button"
              onClick={() => setCloseOpen(true)}
              className="rounded-xl border border-line bg-white/70 px-2.5 py-1.5 text-[12px] font-semibold text-ink hover:bg-white md:px-3 md:py-2 md:text-[13px]"
            >
              Закрыть
            </button>
          ) : null}
          {active && CLOSED.includes(active.status) ? (
            <button
              type="button"
              onClick={() => void reopenConversation()}
              className="rounded-xl bg-accent px-2.5 py-1.5 text-[12px] font-semibold text-white md:px-3 md:py-2 md:text-[13px]"
            >
              Вернуть в работу
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setSidebarOpen((value) => !value)}
            className="hidden h-[38px] items-center gap-2 rounded-xl border border-line bg-white/70 px-3 text-[14px] font-semibold text-ink hover:bg-white md:flex"
          >
            <span className="text-muted">⌘</span>
            {sidebarOpen ? 'Свернуть' : 'Развернуть'}
          </button>
        </header>

        {subscriptionNotice ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gold/30 bg-[#FBF3DE] px-5 py-2.5 text-[13px] font-medium text-[#7A5C14] md:px-7">
            <span>{subscriptionNotice}</span>
            <a href="/tariffs" className="font-semibold text-accent">
              Продлить
            </a>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-3 border-b border-line bg-white/35 px-3 py-3 md:gap-4 md:px-8 md:py-3.5">
          <div className="flex basis-full flex-wrap items-center gap-2 md:flex-nowrap md:basis-auto md:gap-3 md:flex-1">
            <h1 className="basis-full truncate text-[20px] md:basis-auto font-semibold leading-[26px] tracking-[-0.006em] text-ink md:w-auto md:text-[22px] md:leading-[28px]">
              {active
                ? messages.length === 0
                  ? 'Новый вопрос'
                  : (active.subject ?? 'Консультация с юристом')
                : 'Консультация с юристом'}
            </h1>
            {active ? (
              <span
                className={`inline-flex flex-nowrap items-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-1 text-[12px] font-semibold md:px-2.5 md:text-[14px] ${statusTone(active.status)}`}
              >
                {active.status === 'answered' ? (
                  <CheckCircleIcon className="h-3.5 w-3.5" />
                ) : active.status === 'docs_requested' ? (
                  <LockIcon className="h-3.5 w-3.5" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-current" />
                )}
                {statusLabel(active.status)}
              </span>
            ) : null}
            <select
              className="basis-full rounded-lg border border-line bg-white/90 px-2.5 py-1.5 text-xs font-semibold text-ink md:hidden"
              value={active?.id ?? ''}
              onChange={(event) => {
                const conversation = conversations.find((item) => item.id === event.target.value);
                if (conversation) void open(conversation);
              }}
              aria-label="Выбрать диалог"
            >
              {conversations.map((conversation) => (
                <option key={conversation.id} value={conversation.id}>
                  {title(conversation)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <StageBar status={active?.status ?? 'new'} />
            <span className="text-[14px] text-muted">
              {active && CLOSED.includes(active.status)
                ? 'Диалог закрыт'
                : active?.status === 'new'
                  ? 'Ожидаем подключения юриста'
                  : active?.status === 'answered'
                    ? 'Ответ готов — оцените, пожалуйста'
                    : active?.status === 'docs_requested'
                      ? 'Юрист ждёт документы'
                      : 'Ответим в течение часа'}
            </span>
          </div>
        </div>

        <div className="flex min-h-0 flex-1">
          {sidebarOpen ? (
            <aside className="hidden w-[290px] shrink-0 flex-col gap-2 border-r border-line bg-white/50 p-4 md:flex">
              <div className="flex items-center justify-between gap-2 px-1 pb-1">
                <span className="text-[16px] font-semibold text-ink">Мои диалоги</span>
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="flex h-[30px] w-[30px] items-center justify-center rounded-full border border-line bg-white/90 text-muted"
                  aria-label="Свернуть панель"
                >
                  ‹
                </button>
              </div>

              <button
                type="button"
                onClick={() => setNewOpen(true)}
                className="flex h-[50px] items-center justify-center gap-2 rounded-xl bg-ink-gradient px-5 text-[16px] font-semibold text-white"
              >
                <PlusIcon className="h-4 w-4" /> Новый вопрос
              </button>

              <div className="mt-1 flex items-center gap-3 rounded-2xl border-[1.5px] border-gold bg-white/95 px-3 py-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-gold bg-[linear-gradient(126.87deg,#E8F0FF_0%,#F5EBD1_71.43%)] text-[#7A5C14]">
                  <PersonIcon className="h-5 w-5 md:h-6 md:w-6" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-semibold text-[#7A5C14]">ВАШ ЮРИСТ</div>
                  <div className="truncate text-[15px] font-semibold text-ink">
                    {lawyer?.name ?? 'Подбираем юриста'}
                  </div>
                  <div className="flex items-center gap-1.5 text-[12px] font-semibold text-success">
                    <span className={`h-1.5 w-1.5 rounded-full ${lawyer?.online ? 'bg-success' : 'bg-muted-400'}`} />
                    {lawyer?.online ? 'на связи до 21:00' : 'ответит в рабочее время'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 self-end">
                <button
                  type="button"
                  onClick={() => void openCase()}
                  className="text-[12px] font-semibold text-ink"
                >
                  Дело
                </button>
                {active?.lawyerId ? (
                  <button
                    type="button"
                    onClick={() => void openChange()}
                    className="text-[12px] font-semibold text-accent"
                  >
                    Сменить юриста
                  </button>
                ) : null}
              </div>

              <div className="flex items-center gap-2 rounded-xl border border-dashed border-line bg-white/50 px-3 py-3 text-muted">
                <LockIcon className="h-4 w-4 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[14px] font-semibold">Документы</div>
                  <div className="text-[12px]">Приватный кейс · доступ у юриста</div>
                </div>
              </div>

              <div className="mt-2 flex items-center gap-2 px-1">
                <span className="text-[12px] font-semibold uppercase tracking-[0.08em] text-muted">
                  Активные
                </span>
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-line px-1.5 text-[12px] font-semibold text-muted">
                  {activeConversations.length}
                </span>
              </div>
              <div className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-0.5">
                {activeConversations.length === 0 ? (
                  <p className="px-2 py-3 text-[13px] text-muted">Пока нет активных диалогов</p>
                ) : (
                  activeConversations.map((conversation) => (
                    <button
                      key={conversation.id}
                      type="button"
                      onClick={() => void open(conversation)}
                      className={`flex w-full items-start gap-2.5 rounded-[10px] border px-3 py-2.5 text-left transition-colors ${
                        active?.id === conversation.id
                          ? 'border-accent bg-white/96'
                          : 'border-transparent hover:bg-white/60'
                      }`}
                    >
                      <span
                        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                          conversation.status === 'new'
                            ? 'bg-accent'
                            : conversation.status === 'docs_requested'
                              ? 'bg-gold'
                              : conversation.status === 'answered'
                                ? 'bg-success'
                                : 'bg-accent'
                        }`}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[14.5px] font-semibold text-ink">
                          {title(conversation)}
                        </span>
                        <span className="block truncate text-[12.5px] text-muted">
                          {conversation.lastMessage?.hasAttachments ? '📎 ' : ''}
                          {conversation.lastMessage?.text ?? statusLabel(conversation.status)}
                        </span>
                      </span>
                      {conversation.unreadByClient > 0 ? (
                        <span className="mt-0.5 flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-accent px-1 text-[12px] font-bold text-white">
                          {conversation.unreadByClient}
                        </span>
                      ) : null}
                    </button>
                  ))
                )}

                {historyConversations.length > 0 ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setHistoryOpen((value) => !value)}
                      className="mt-3 flex w-full items-center gap-2 rounded-lg px-1 py-1.5 hover:bg-white/60"
                      aria-expanded={historyOpen}
                    >
                      <span className="text-[12px] font-semibold uppercase tracking-[0.08em] text-muted">
                        Завершённые
                      </span>
                      <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-line px-1.5 text-[12px] font-semibold text-muted">
                        {historyConversations.length}
                      </span>
                      <span className="ml-auto text-[13px] text-muted">{historyOpen ? '⌃' : '⌄'}</span>
                    </button>
                    {historyOpen
                      ? historyConversations.map((conversation) => (
                          <button
                            key={conversation.id}
                            type="button"
                            onClick={() => void open(conversation)}
                            className={`flex w-full items-start gap-2.5 rounded-[10px] px-3 py-2.5 text-left hover:bg-white/60 ${
                              active?.id === conversation.id ? 'border border-accent bg-white/96' : ''
                            }`}
                          >
                            <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-line" />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-[14.5px] font-semibold text-ink">
                                {title(conversation)}
                              </span>
                              <span className="block truncate text-[12.5px] text-muted">
                                {conversation.lastMessage?.text ?? 'Закрыт'}
                              </span>
                            </span>
                          </button>
                        ))
                      : null}
                  </>
                ) : null}
              </div>
            </aside>
          ) : (
            <aside className="hidden w-[68px] shrink-0 flex-col items-center gap-2.5 border-r border-line bg-white/50 px-3 py-4 md:flex">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="flex h-[30px] w-[30px] items-center justify-center rounded-full border border-line bg-white/90 text-muted"
                aria-label="Развернуть панель"
              >
                ›
              </button>
              <button
                type="button"
                onClick={() => setNewOpen(true)}
                className="flex h-11 w-11 items-center justify-center rounded-[14px] bg-ink-gradient text-white"
                aria-label="Новый вопрос"
              >
                <PlusIcon className="h-5 w-5" />
              </button>
              <span className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-gold bg-[linear-gradient(126.87deg,#E8F0FF_0%,#F5EBD1_71.43%)] text-[#7A5C14]">
                <PersonIcon className="h-5 w-5 md:h-6 md:w-6" />
              </span>
              <span className="h-px w-8 bg-line" />
              <div className="flex w-full flex-1 flex-col items-center gap-2.5 overflow-y-auto">
                {conversations.slice(0, 8).map((conversation) => (
                  <button
                    key={conversation.id}
                    type="button"
                    onClick={() => void open(conversation)}
                    className={`relative flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] border ${
                      active?.id === conversation.id
                        ? 'border-[1.5px] border-accent bg-white/96'
                        : 'border-line bg-white/70'
                    }`}
                    aria-label={title(conversation)}
                  >
                    <span
                      className={`h-[11px] w-[11px] rounded-full ${
                        conversation.status === 'answered'
                          ? 'bg-success'
                          : conversation.status === 'docs_requested'
                            ? 'bg-gold'
                            : CLOSED.includes(conversation.status)
                              ? 'bg-line'
                              : 'bg-accent'
                      }`}
                    />
                    {conversation.unreadByClient > 0 ? (
                      <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-white">
                        {conversation.unreadByClient}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            </aside>
          )}

          <section className="flex min-w-0 flex-1 justify-center bg-white/10">
            <div className="flex min-h-0 w-full max-w-[780px] flex-col">
              {!active ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-soft text-accent">
                    <PersonIcon className="h-7 w-7" />
                  </span>
                  <p className="text-[17px] font-semibold text-ink">Начните диалог с юристом</p>
                  <p className="max-w-sm text-sm text-muted">
                    Задайте вопрос — мы подберём юриста и ответим в рабочее время.
                  </p>
                  <Button variant="accent" onClick={() => setNewOpen(true)}>
                    Новый вопрос
                  </Button>
                </div>
              ) : (
                <>
                  <div ref={messagesRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-6 md:px-6">
                    {chatNotice ? (
                      <div className="mb-4 rounded-2xl border border-gold/30 bg-[#FBF3DE] px-4 py-3 text-[13.5px] font-medium leading-5 text-[#7A5C14]">
                        {chatNotice}
                      </div>
                    ) : null}
                    {active?.status === 'reopened' ? (
                      <div className="mb-4 flex items-center gap-3 rounded-2xl bg-success-soft px-4 py-3.5">
                        <CheckCircleIcon className="h-5 w-5 shrink-0 text-success" />
                        <div>
                          <div className="text-[16px] font-semibold text-success">
                            Обращение возвращено в работу
                          </div>
                          <div className="text-[14px] text-ink">
                            Юрист получил вопрос повторно и ответит в рабочее время.
                          </div>
                        </div>
                      </div>
                    ) : null}
                    {active && CLOSED.includes(active.status) ? (
                      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl bg-soft px-4 py-3.5">
                        <LockIcon className="h-5 w-5 shrink-0 text-muted" />
                        <div className="min-w-0 flex-1">
                          <div className="text-[16px] font-semibold text-ink">Обращение закрыто</div>
                          <div className="text-[14px] text-muted">
                            Переписка сохранена. Если вопрос снова актуален — верните обращение в работу.
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => void reopenConversation()}
                          className="rounded-xl bg-accent px-4 py-2.5 text-[14px] font-semibold text-white"
                        >
                          Вернуть в работу
                        </button>
                      </div>
                    ) : null}
                    {messages.length > 0 &&
                    active &&
                    ['new', 'in_progress', 'waiting_lawyer', 'docs_requested', 'reopened'].includes(
                      active.status
                    ) ? (
                      <div className="mb-4 rounded-2xl border border-line bg-white/80 p-4 text-[14px] text-ink">
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-semibold">Вопрос получен</span>
                          <span className="text-[12px] text-muted">
                            {firstClientMessage
                              ? new Date(firstClientMessage.createdAt).toLocaleTimeString('ru-RU', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : ''}
                          </span>
                        </div>
                        <div className="mt-2 flex items-center gap-2 text-[13px]">
                          <span className={hasClientDocuments ? 'text-success' : 'text-[#D6452D]'}>
                            {hasClientDocuments ? 'Документ приложен' : 'Документ не приложен'}
                          </span>
                          {!hasClientDocuments ? (
                            <label className="cursor-pointer font-semibold text-accent">
                              Добавить
                              <input
                                type="file"
                                multiple
                                className="hidden"
                                onChange={(event) => {
                                  setFiles((prev) => [...prev, ...Array.from(event.target.files ?? [])]);
                                  event.target.value = '';
                                }}
                              />
                            </label>
                          ) : null}
                        </div>
                        <div className="mt-2 text-[13px]">
                          Юрист ответит{' '}
                          <span className="font-semibold">
                            {active.answerDueAt
                              ? `до ${new Date(active.answerDueAt).toLocaleString('ru-RU', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}`
                              : 'в рабочее время'}
                          </span>
                        </div>
                        <p className="mt-2 text-[12px] leading-[19px] text-muted">
                          Пока ждёте: ничего не подписывайте и не отправляйте в ведомство, пока не
                          получите ответ юриста.
                        </p>
                      </div>
                    ) : null}
                    {messages.length === 0 ? (
                      <div className="rounded-2xl border border-line bg-white/95 p-4 shadow-sm">
                        <div className="mb-3 text-[15px] font-semibold text-ink">Частые вопросы</div>
                        <div className="flex flex-col gap-2">
                          {quickReplies.map((reply) => (
                            <button
                              key={reply}
                              type="button"
                              onClick={() => setDraft(reply)}
                              className="flex items-center justify-between gap-3 rounded-[10px] border border-line bg-white/90 px-3.5 py-2.5 text-left text-[14px] font-medium text-ink hover:bg-soft"
                            >
                              {reply}
                              <span className="text-accent">→</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      messages.map((message) => {
                        if (message.senderRole === 'system') {
                          return (
                            <div key={message.id} className="mb-3 flex justify-center">
                              <span className="rounded-full bg-white/70 px-3 py-1 text-xs text-muted">
                                {message.text}
                              </span>
                            </div>
                          );
                        }
                        const mine = message.senderRole === 'client';
                        const answer = mine ? null : message.answer;
                        return (
                          <div
                            key={message.id}
                            className={`mb-3 flex ${mine ? 'justify-end' : 'justify-start'} ${
                              answer ? 'gap-3' : ''
                            }`}
                          >
                            {answer ? (
                              <span className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent sm:flex">
                                <PersonIcon className="h-5 w-5" />
                              </span>
                            ) : null}
                            <div
                              className={`space-y-1 ${
                                answer ? 'w-full max-w-[780px] items-start' : 'max-w-[78%]'
                              } ${mine ? 'items-end' : 'items-start'}`}
                            >
                              {answer ? (
                                active?.rating && !expandedAnswers[message.id] ? (
                                  <div className="flex w-full flex-wrap items-center gap-3 rounded-2xl border border-line bg-white/90 px-4 py-3">
                                    <span className="min-w-0 flex-1 text-[15px] font-semibold text-ink">
                                      Ответ юриста
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setExpandedAnswers((prev) => ({ ...prev, [message.id]: true }))
                                      }
                                      className="text-[14px] font-semibold text-accent"
                                    >
                                      Развернуть
                                    </button>
                                  </div>
                                ) : (
                                <div className="rounded-2xl border border-line bg-white/95 p-4 md:px-[22px] md:py-[18px]">
                                  <div className="flex items-start gap-3">
                                    <p className="min-w-0 flex-1 text-[16px] leading-6 text-ink">
                                      {answer.summary || message.text}
                                    </p>
                                    {active?.rating ? (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setExpandedAnswers((prev) => ({ ...prev, [message.id]: false }))
                                        }
                                        className="shrink-0 text-[13px] font-semibold text-accent"
                                      >
                                        Свернуть
                                      </button>
                                    ) : null}
                                  </div>
                                  {answer.steps.length > 0 ? (
                                    <>
                                      <div className="mt-4 text-[12px] font-semibold uppercase tracking-[0.08em] text-muted">
                                        Что делать дальше
                                      </div>
                                      <div className="mt-2 space-y-2.5">
                                        {answer.steps.map((step, index) => (
                                          <div key={`${message.id}-${index}`} className="flex items-start gap-3">
                                            <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-accent-soft text-[13px] font-bold text-accent">
                                              {index + 1}
                                            </span>
                                            <span className="pt-0.5 text-[15px] leading-[22px] text-ink">
                                              {step}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </>
                                  ) : null}
                                  {answer.deadline || answer.reminder ? (
                                    <div className="mt-3 flex flex-wrap items-center gap-2">
                                      {answer.deadline ? (
                                        <span className="inline-flex items-center gap-1.5 rounded-lg bg-[#FBF3DE] px-2.5 py-1 text-[13px] font-semibold text-[#7A5C14]">
                                          Срок: {answer.deadline}
                                        </span>
                                      ) : null}
                                      {answer.reminder ? (
                                        <span className="inline-flex items-center gap-1.5 rounded-lg bg-accent-soft px-2.5 py-1 text-[13px] font-semibold text-accent">
                                          {answer.reminder}
                                        </span>
                                      ) : null}
                                    </div>
                                  ) : null}
                                  {message.attachments.length > 0 ? (
                                    <div className="mt-3 space-y-1">
                                      {message.attachments.map((attachment) => (
                                        <a
                                          key={attachment.id}
                                          href={attachment.url || undefined}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="flex w-fit items-center gap-2 rounded-lg border border-line bg-soft px-2.5 py-1.5 text-xs text-ink"
                                        >
                                          <AttachIcon className="h-3.5 w-3.5" />
                                          <span className="truncate">{attachment.name}</span>
                                        </a>
                                      ))}
                                    </div>
                                  ) : null}
                                  <div className="mt-4 flex flex-wrap items-center gap-3">
                                    {answer.verified ? (
                                      <span className="inline-flex items-center gap-1.5 rounded-lg bg-[#FBF3DE] px-2.5 py-1 text-[13px] font-semibold text-[#7A5C14]">
                                        <CheckCircleIcon className="h-3.5 w-3.5" />
                                        Ответ проверил и подписал юрист
                                      </span>
                                    ) : null}
                                    <button
                                      type="button"
                                      onClick={() => downloadAnswer(message)}
                                      className="ml-auto rounded-xl border-[1.5px] border-line bg-white px-4 py-2.5 text-[15px] font-semibold text-ink hover:bg-soft"
                                    >
                                      Скачать памятку
                                    </button>
                                  </div>
                                </div>
                                )
                              ) : (
                                <div
                                  className={`rounded-2xl px-4 py-2.5 text-[15px] leading-6 ${
                                    mine
                                      ? 'rounded-br-md bg-ink text-white'
                                      : 'rounded-bl-md border border-line bg-white/95 text-ink'
                                  }`}
                                >
                                  {message.text}
                                  {message.attachments.length > 0 ? (
                                    <div className="mt-2 space-y-1">
                                      {message.attachments.map((attachment) =>
                                        attachment.mimeType.startsWith('image/') && attachment.url ? (
                                          <a
                                            key={attachment.id}
                                            href={attachment.url}
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            <img
                                              src={attachment.url}
                                              alt={attachment.name}
                                              className="max-h-52 w-full rounded-xl object-cover"
                                            />
                                          </a>
                                        ) : (
                                          <a
                                            key={attachment.id}
                                            href={attachment.url || undefined}
                                            target="_blank"
                                            rel="noreferrer"
                                            className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs ${
                                              mine
                                                ? 'border-white/25 bg-white/10 text-white'
                                                : 'border-line bg-soft text-ink'
                                            }`}
                                          >
                                            <AttachIcon className="h-3.5 w-3.5" />
                                            <span className="truncate">{attachment.name}</span>
                                          </a>
                                        )
                                      )}
                                    </div>
                                  ) : null}
                                </div>
                              )}
                              <div className="flex items-center gap-2 text-[11px] text-muted">
                                <span>
                                  {new Date(message.createdAt).toLocaleTimeString('ru-RU', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </span>
                                {message.status === 'pending' ? <span>Отправляется…</span> : null}
                                {message.status === 'failed' ? (
                                  <>
                                    <span className="text-danger">Не отправлено</span>
                                    <button
                                      type="button"
                                      onClick={() => void send(message.text ?? '', message)}
                                      className="font-semibold text-accent"
                                    >
                                      Повторить
                                    </button>
                                  </>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    {active?.status === 'answered' && !active.rating ? (
                      <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-line bg-white/95 px-5 py-4">
                        <span className="min-w-0 flex-1 text-[16px] font-semibold text-ink">
                          Ответ помог?
                        </span>
                        <button
                          type="button"
                          disabled={ratingBusy}
                          onClick={() => void rateAnswer(true)}
                          className="rounded-xl bg-accent-gradient px-6 py-2.5 text-[15px] font-semibold text-white disabled:opacity-60"
                        >
                          Да
                        </button>
                        <button
                          type="button"
                          disabled={ratingBusy}
                          onClick={() => {
                            setFeedbackReason('not_clear');
                            setFeedbackOpen(true);
                          }}
                          className="rounded-xl border-[1.5px] border-line bg-white px-6 py-2.5 text-[15px] font-semibold text-ink disabled:opacity-60"
                        >
                          Не совсем
                        </button>
                      </div>
                    ) : null}
                    {active?.rating ? (
                      <div className="mt-4 rounded-2xl border border-line bg-white/90 px-5 py-4">
                        <div className="flex items-center gap-2.5 text-[16px] font-semibold text-ink">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-success-soft text-success">
                            <CheckIcon className="h-3.5 w-3.5" />
                          </span>
                          {active.rating === 'helped'
                            ? 'Спасибо! Рады, что смогли помочь'
                            : active.feedbackReason === 'refund'
                              ? 'Заявка на возврат принята'
                              : 'Спасибо, юрист получил уточнение'}
                        </div>
                        <p className="mt-1.5 text-[14px] leading-[20px] text-muted">
                          {active.rating === 'helped'
                            ? 'Обращение останется открытым: если появятся вопросы — просто напишите в чат.'
                            : 'Юрист вернётся к ответу и уточнит детали. Вы можете дописать вопрос или приложить документы.'}
                        </p>
                      </div>
                    ) : null}
                    {typing ? (
                      <div className="flex items-center gap-2.5 rounded-2xl border border-line bg-white/95 px-3.5 py-3 text-[15px] text-muted">
                        <Orb className="h-5 w-5" />
                        юрист уже читает ваш вопрос
                      </div>
                    ) : null}
                  </div>

                  <div className="border-t border-line bg-white/35 p-3 md:p-4">
                    {files.length > 0 ? (
                      <div className="mb-2 flex flex-wrap gap-2">
                        {files.map((file, index) => (
                          <span
                            key={`${file.name}-${index}`}
                            className="flex items-center gap-2 rounded-lg bg-white px-2.5 py-1.5 text-xs text-ink"
                          >
                            <AttachIcon className="h-3.5 w-3.5 text-muted" />
                            {file.name}
                            <button
                              type="button"
                              onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                              className="text-muted hover:text-danger"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {active && CLOSED.includes(active.status) ? (
                      <div className="mb-2 rounded-xl border border-line bg-soft px-3 py-2 text-xs font-medium text-muted">
                        Обращение закрыто. Читать можно, писать — нельзя.
                      </div>
                    ) : null}
                    {error ? <p className="mb-2 text-xs text-danger">{error}</p> : null}
                    <div className="flex items-center gap-2.5 rounded-2xl border border-line bg-white/95 p-2 pl-4">
                      <Orb className="h-5 w-5 shrink-0" />
                      <textarea
                        value={draft}
                        rows={1}
                        disabled={Boolean(active && CLOSED.includes(active.status))}
                        placeholder="Опишите вашу ситуацию…"
                        onChange={(event) => {
                          setDraft(event.target.value);
                          emitTyping(true);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' && !event.shiftKey) {
                            event.preventDefault();
                            void send(draft);
                          }
                        }}
                        className="max-h-28 min-h-9 flex-1 resize-none bg-transparent px-1 py-1.5 text-[16px] text-ink outline-none placeholder:text-muted-400"
                      />
                      <label className="flex h-[38px] cursor-pointer items-center gap-1.5 rounded-[10px] bg-soft px-3 text-[14px] font-medium text-muted hover:text-ink">
                        <AttachIcon className="h-4 w-4" />
                        <span className="hidden sm:inline">Документ</span>
                        <input
                          type="file"
                          multiple
                          className="hidden"
                          onChange={(event) => {
                            setFiles((prev) => [...prev, ...Array.from(event.target.files ?? [])]);
                            event.target.value = '';
                          }}
                        />
                      </label>
                      <button
                        type="button"
                        disabled={Boolean(active && CLOSED.includes(active.status)) || sending || (!draft.trim() && files.length === 0)}
                        onClick={() => void send(draft)}
                        className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-xl bg-accent text-white disabled:bg-line disabled:text-muted-400"
                        aria-label="Отправить"
                      >
                        <SendIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </section>
        </div>
      </div>

      {newOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/25 px-4 backdrop-blur-sm">
          <div className="w-full max-w-xl rounded-3xl border border-white/80 bg-white p-6 shadow-chat">
            <div className="mb-1 flex items-center justify-between">
              <h2 className="text-[26px] font-semibold leading-[33px] tracking-[-0.008em] text-ink">
                Новый вопрос
              </h2>
              <button type="button" onClick={() => setNewOpen(false)} className="text-xl text-muted">
                ×
              </button>
            </div>
            <p className="mb-4 text-[14px] leading-[20px] text-muted">
              Ответит военный юрист. Чем точнее опишете ситуацию, тем быстрее поможет.
            </p>

            <div className="text-[15px] font-semibold text-ink">Для кого вопрос</div>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              {AUDIENCES.map((item) => {
                const selected = audience === item.value;
                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => {
                      setAudience(item.value);
                      setNewError(null);
                    }}
                    className={`rounded-2xl border px-3.5 py-3 text-left transition-colors ${
                      selected
                        ? 'border-2 border-accent bg-accent-soft'
                        : 'border-line bg-white/95 hover:bg-soft'
                    }`}
                  >
                    <span className="block text-[14px] font-semibold text-ink">{item.title}</span>
                    <span className="mt-1 block text-[12px] leading-[16px] text-muted">{item.sub}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1.5 text-sm">
                <span className="font-semibold text-muted">Тема</span>
                <select
                  value={topic}
                  onChange={(event) => setTopic(event.target.value as ConversationTopic)}
                  className="rounded-xl border border-line bg-white px-3 py-2.5 text-sm text-ink"
                >
                  {TOPICS.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1.5 text-sm">
                <span className="font-semibold text-muted">Срок ответа</span>
                <select
                  value={responseHours}
                  onChange={(event) => setResponseHours(Number(event.target.value) as 1 | 3 | 5)}
                  className="rounded-xl border border-line bg-white px-3 py-2.5 text-sm text-ink"
                >
                  <option value={1}>1 рабочий час</option>
                  <option value={3}>3 рабочих часа</option>
                  <option value={5}>5 рабочих часов</option>
                </select>
              </label>
            </div>

            {audience === 'relative' ? (
              <label
                className={`mt-4 flex items-start gap-2.5 text-[14px] leading-[20px] ${
                  newError ? 'text-danger' : 'text-muted'
                }`}
              >
                <input
                  type="checkbox"
                  checked={relativeConsent}
                  onChange={(event) => {
                    setRelativeConsent(event.target.checked);
                    setNewError(null);
                  }}
                  className="mt-0.5 h-5 w-5 shrink-0 accent-accent"
                />
                <span>
                  Близкий человек знает о вопросе и согласен передать документы юристу
                </span>
              </label>
            ) : null}

            {newError ? <p className="mt-2 text-[13px] font-medium text-danger">{newError}</p> : null}
            <p className="mt-4 text-[13px] leading-[17px] text-muted">
              Вопрос попадёт в чат. Оплата и возврат обсуждаются отдельно, если понадобятся.
            </p>
            <button
              type="button"
              onClick={() => void createConversation()}
              disabled={creating}
              className="mt-3 w-full rounded-xl bg-ink-gradient px-4 py-3.5 text-[16px] font-semibold text-white disabled:opacity-60"
            >
              {creating ? 'Создаём…' : 'Создать обращение'}
            </button>
          </div>
        </div>
      ) : null}

      {closeOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/25 px-4 backdrop-blur-sm">
          <div className="w-full max-w-[560px] rounded-[32px] border-[1.5px] border-white/95 bg-white/96 p-[30px] shadow-chat">
            <h2 className="text-[28px] font-semibold leading-[36px] tracking-[-0.01em] text-ink">
              Закрыть обращение?
            </h2>
            <p className="mt-2 text-[16px] leading-6 text-muted">
              Переписка сохранится. Если вопрос снова станет актуальным, вы сможете вернуть
              обращение в работу.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3.5">
              <button
                type="button"
                onClick={() => void closeConversation()}
                className="rounded-xl bg-accent-gradient px-6 py-3.5 text-[16px] font-semibold text-white"
              >
                Закрыть обращение
              </button>
              <button
                type="button"
                onClick={() => setCloseOpen(false)}
                className="text-[15px] font-semibold text-accent"
              >
                Продолжить общение
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {feedbackOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/25 px-4 backdrop-blur-sm">
          <div className="max-h-[92vh] w-full max-w-[640px] overflow-y-auto rounded-[32px] border-[1.5px] border-white/95 bg-white/96 p-[30px] shadow-chat">
            <h2 className="text-[28px] font-semibold leading-[36px] tracking-[-0.01em] text-ink">
              Что пошло не так?
            </h2>
            <p className="mt-1 text-[15px] leading-[19px] text-muted">
              Выберите ближе всего: юрист увидит и ответит
            </p>
            <div className="mt-4 space-y-2">
              {FEEDBACK_REASONS.map((item) => {
                const selected = feedbackReason === item.value;
                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => setFeedbackReason(item.value)}
                    className={`flex w-full items-start gap-3.5 rounded-2xl border px-4 py-3.5 text-left ${
                      selected
                        ? 'border-2 border-accent bg-accent-soft'
                        : 'border-line bg-white/90 hover:bg-soft'
                    }`}
                  >
                    <span
                      className={`mt-0.5 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full ${
                        selected ? 'bg-accent' : 'border-2 border-line'
                      }`}
                    >
                      {selected ? <span className="h-2 w-2 rounded-full bg-white" /> : null}
                    </span>
                    <span>
                      <span className="block text-[16px] font-semibold text-ink">{item.title}</span>
                      <span className="mt-0.5 block text-[13px] leading-[18px] text-muted">
                        {item.sub}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            <textarea
              value={feedbackComment}
              onChange={(event) => setFeedbackComment(event.target.value)}
              rows={2}
              placeholder="Комментарий для юриста (необязательно)"
              className="mt-3 w-full resize-none rounded-2xl border border-line bg-white/95 px-4 py-3 text-[15px] text-ink outline-none placeholder:text-muted-400"
            />
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                disabled={ratingBusy}
                onClick={() => void rateAnswer(false)}
                className="rounded-xl bg-accent-gradient px-6 py-3.5 text-[16px] font-semibold text-white disabled:opacity-60"
              >
                Отправить
              </button>
              <button
                type="button"
                onClick={() => setFeedbackOpen(false)}
                className="rounded-xl border-[1.5px] border-line bg-white px-6 py-3.5 text-[16px] font-semibold text-ink"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {changeOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/25 px-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-white/80 bg-white p-6 shadow-chat">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-ink">Сменить юриста</h2>
              <button type="button" onClick={() => setChangeOpen(false)} className="text-muted">
                ×
              </button>
            </div>

            {changeRequest ? (
              <div className="space-y-3">
                <div className="rounded-2xl bg-accent-soft px-4 py-3">
                  <p className="text-[15px] font-semibold text-ink">Заявка отправлена</p>
                  <p className="mt-1 text-[13px] leading-[18px] text-muted">
                    Менеджер подберёт нового юриста и перенесёт все активные обращения.
                    Обычно это занимает до рабочего дня.
                  </p>
                </div>
                <div className="rounded-2xl border border-line px-4 py-3">
                  <div className="text-[12px] font-semibold uppercase tracking-[0.06em] text-muted">
                    Причина
                  </div>
                  <div className="mt-1 text-[15px] text-ink">
                    {CHANGE_REASONS.find((item) => item.value === changeRequest.reason)?.title ??
                      'Другая причина'}
                  </div>
                  {changeRequest.comment ? (
                    <div className="mt-1 text-[13px] leading-[18px] text-muted">
                      {changeRequest.comment}
                    </div>
                  ) : null}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    disabled={changeBusy}
                    onClick={() => void cancelChangeRequest()}
                    className="rounded-xl border-[1.5px] border-line bg-white px-5 py-3 text-[15px] font-semibold text-ink disabled:opacity-60"
                  >
                    Отменить заявку
                  </button>
                  <button
                    type="button"
                    onClick={() => setChangeOpen(false)}
                    className="px-2 py-3 text-[15px] font-semibold text-accent"
                  >
                    Закрыть
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="space-y-2">
                  {CHANGE_REASONS.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => setChangeReason(item.value)}
                      className={`flex w-full items-center gap-3 rounded-2xl px-3.5 py-3 text-left transition-colors ${
                        changeReason === item.value
                          ? 'border-2 border-accent bg-accent-soft'
                          : 'border border-line bg-white/90'
                      }`}
                    >
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                          changeReason === item.value ? 'bg-accent' : 'border-2 border-line'
                        }`}
                      >
                        {changeReason === item.value ? (
                          <span className="h-2 w-2 rounded-full bg-white" />
                        ) : null}
                      </span>
                      <span>
                        <span className="block text-[15px] font-semibold text-ink">{item.title}</span>
                        <span className="mt-0.5 block text-[13px] leading-[18px] text-muted">
                          {item.sub}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>

                <label className="block space-y-1.5">
                  <span className="text-[13px] font-semibold text-muted">Желаемый юрист (необязательно)</span>
                  <select
                    value={preferredLawyerId}
                    onChange={(event) => setPreferredLawyerId(event.target.value)}
                    className="w-full rounded-2xl border border-line bg-white px-3 py-3 text-[15px] text-ink"
                  >
                    <option value="">Подберёт менеджер</option>
                    {lawyers.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name ?? 'Юрист'} · свободно {item.availableSlots}
                      </option>
                    ))}
                  </select>
                </label>

                <textarea
                  value={changeComment}
                  onChange={(event) => setChangeComment(event.target.value)}
                  rows={3}
                  placeholder="Что не подошло? Это увидит только менеджер"
                  className="w-full resize-none rounded-2xl border border-line bg-white px-4 py-3 text-[15px] text-ink outline-none placeholder:text-muted-400"
                />

                <p className="text-[12px] leading-[17px] text-muted">
                  Юриста меняет менеджер. Все активные обращения перейдут к новому юристу,
                  закрытые останутся в истории.
                </p>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    disabled={changeBusy}
                    onClick={() => void submitChangeRequest()}
                    className="rounded-xl bg-ink-gradient px-6 py-3.5 text-[16px] font-semibold text-white disabled:opacity-60"
                  >
                    Отправить заявку
                  </button>
                  <button
                    type="button"
                    onClick={() => setChangeOpen(false)}
                    className="rounded-xl border-[1.5px] border-line bg-white px-6 py-3.5 text-[16px] font-semibold text-ink"
                  >
                    Отмена
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {caseOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/25 px-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-white/80 bg-white p-6 shadow-chat">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-ink">Дело</h2>
                <p className="text-[13px] text-muted">{caseDetail?.title ?? 'Обращение'}</p>
              </div>
              <button type="button" onClick={() => setCaseOpen(false)} className="text-muted">
                ×
              </button>
            </div>
            {caseDetail ? (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-1.5">
                  {([
                    ['accepted', 'Принято'],
                    ['documents', 'Документы'],
                    ['draft', 'Черновик'],
                    ['filed', 'Подано'],
                    ['response', 'Ответ'],
                    ['completed', 'Завершено'],
                  ] as const).map(([value, label], index, all) => (
                    <span
                      key={value}
                      className={`rounded-lg px-2.5 py-1.5 text-[12px] font-semibold ${
                        value === caseDetail.stage
                          ? 'bg-accent text-white'
                          : all.findIndex(([item]) => item === caseDetail.stage) > index
                            ? 'bg-success-soft text-success'
                            : 'bg-soft text-muted'
                      }`}
                    >
                      {label}
                    </span>
                  ))}
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-line px-4 py-3">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
                      Срок ответа
                    </div>
                    <div className="mt-1 text-[14px] text-ink">
                      {caseDetail.responseDueAt
                        ? new Date(caseDetail.responseDueAt).toLocaleDateString('ru-RU')
                        : 'не указан'}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-line px-4 py-3">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
                      Срок обжалования
                    </div>
                    <div className="mt-1 text-[14px] text-ink">
                      {caseDetail.appealUntil
                        ? new Date(caseDetail.appealUntil).toLocaleDateString('ru-RU')
                        : 'не указан'}
                    </div>
                  </div>
                </div>

                <div>
                  <a
                  href={`/cases/${caseDetail.conversationId}`}
                  className="mb-3 inline-block text-[13px] font-semibold text-accent"
                >
                  Открыть дело полностью →
                </a>
                <h3 className="mb-2 text-[15px] font-semibold text-ink">Черновик жалобы</h3>
                  {caseDetail.draftVersions.length > 0 ? (
                    <>
                      <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-2xl bg-soft px-4 py-3 font-sans text-[13.5px] leading-6 text-ink">
                        {caseDetail.draftVersions.at(-1)?.body}
                      </pre>
                      <p className="mt-1 text-[11px] text-muted">
                        Версия {caseDetail.draftVersions.at(-1)?.version} ·{' '}
                        {caseDetail.draftVersions.at(-1)?.authorName ?? 'юрист'}
                      </p>
                    </>
                  ) : (
                    <p className="text-[13px] text-muted">Юрист ещё готовит черновик.</p>
                  )}
                </div>

                {caseDetail.draftVersions.length > 0 ? (
                  <div>
                    <textarea
                      value={caseComment}
                      onChange={(event) => setCaseComment(event.target.value)}
                      rows={3}
                      placeholder="Что поправить в черновике?"
                      className="w-full resize-none rounded-2xl border border-line bg-white px-4 py-3 text-[14px] text-ink outline-none placeholder:text-muted-400"
                    />
                    <div className="mt-2 flex items-center gap-3">
                      <button
                        type="button"
                        disabled={caseBusy || !caseComment.trim()}
                        onClick={() => void commentCaseDraft()}
                        className="rounded-xl bg-accent px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                      >
                        Отправить правки
                      </button>
                      {caseDetail.clientDraftComment ? (
                        <span className="text-[12px] text-muted">Правки отправлены</span>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-muted">Загружаем дело…</p>
            )}
          </div>
        </div>
      ) : null}

      {changeNotice ? (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm rounded-2xl border border-success/30 bg-success-soft px-4 py-3 text-sm font-medium text-ink shadow-[0_18px_50px_rgba(10,28,64,0.18)]">
          {changeNotice}
        </div>
      ) : null}
    </div>
  );
}
